import { useTranslation } from 'react-i18next'

interface ProgressIndicatorProps {
  currentStep: number
  totalSteps: number
}

/**
 * Progress indicator for multi-step onboarding (FR-003)
 * Shows grouped dots for loved one (4 steps) and caregiver (1 step)
 */
export function ProgressIndicator({ currentStep, totalSteps }: ProgressIndicatorProps) {
  const { t } = useTranslation()

  const containerStyles = {
    marginBottom: '2rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const textStyles = {
    fontSize: '14px',
    color: '#666',
    marginBottom: '0.75rem',
    textAlign: 'center' as const,
  }

  const dotsContainerStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  }

  const groupStyles = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  }

  const dotStyles = (isActive: boolean, isCompleted: boolean) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: isCompleted || isActive ? '#007AFF' : '#e0e0e0',
    transition: 'background-color 0.3s ease',
    border: isActive ? '2px solid #007AFF' : 'none',
    outline: isActive ? '2px solid #007AFF40' : 'none',
    outlineOffset: '2px',
  })

  const labelStyles = {
    fontSize: '11px',
    color: '#999',
    fontWeight: 600,
    letterSpacing: '0.5px',
  }

  // Steps 1-4 are for loved one, step 5 is for caregiver
  const lovedOneSteps = [1, 2, 3, 4]
  const caregiverSteps = [5]

  return (
    <div style={containerStyles}>
      <div style={textStyles}>
        {t('onboarding.progress.step', { current: currentStep, total: totalSteps })}
      </div>
      <div style={dotsContainerStyles}>
        {/* Loved One Group */}
        <div style={groupStyles}>
          <span style={labelStyles}>❤️</span>
          {lovedOneSteps.map(step => (
            <div key={step} style={dotStyles(step === currentStep, step < currentStep)} />
          ))}
        </div>

        {/* Divider */}
        <div style={{ width: '16px', height: '1px', backgroundColor: '#e0e0e0' }} />

        {/* Caregiver Group */}
        <div style={groupStyles}>
          <span style={labelStyles}>🤝</span>
          {caregiverSteps.map(step => (
            <div key={step} style={dotStyles(step === currentStep, step < currentStep)} />
          ))}
        </div>
      </div>
    </div>
  )
}
